"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
/**
 * Hook that triggers a callback when a click or touch event happens outside
 * of the provided element(s).
 *
 * @param refs - A single React ref or an array of React refs pointing to HTMLElement(s).
 *               These elements are considered 'inside'. Clicking inside any of them
 *               will NOT trigger the callback.
 * @param callback - Function to call when a click/touch occurs outside all referenced elements.
 * @param isActive - Optional boolean to enable or disable the hook. Defaults to `true`.
 *
 * @example
 * ```tsx
 * const ref1 = useRef<HTMLDivElement>(null)
 * const ref2 = useRef<HTMLDivElement>(null)
 * 
 * useOutsideClick([ref1, ref2], () => {
 *   console.log('Clicked outside both elements!')
 * })
 * ```
 */
function useOutsideClick(refs, callback) {
  let isActive = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  // Normalize refs to an array for consistent processing
  const refsArray = Array.isArray(refs) ? refs : [refs];
  const handleClickOutside = (0, _react.useCallback)(event => {
    // Check if the event target is inside any of the refs
    const isInsideSomeRef = refsArray.some(ref => ref.current && ref.current.contains(event.target));
    if (!isInsideSomeRef && isActive) {
      callback();
    }
  }, [refsArray, callback, isActive]);
  (0, _react.useEffect)(() => {
    if (!isActive) return;
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('pointerdown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [handleClickOutside, isActive]);
}
var _default = exports.default = useOutsideClick;